import React, { useState } from "react";
import { api, storeAuth } from "../api/client";

const appName = process.env.REACT_APP_APP_NAME || "Jasmine";

export default function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("Super Admin");
  const [showBootstrap, setShowBootstrap] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await api.login({ email, password, device_name: "Web" });
      storeAuth(result);
      onLogin(result.user);
    } catch (err) {
      setError(err.message || "Login gagal. Periksa email dan password.");
    } finally {
      setLoading(false);
    }
  }

  async function bootstrap(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.bootstrapSuperAdmin({ full_name: fullName, email, password });
      const result = await api.login({ email, password, device_name: "Web" });
      storeAuth(result);
      onLogin(result.user);
    } catch (err) {
      setError(err.message || "Gagal membuat Super Admin pertama.");
    } finally {
      setLoading(false);
    }
  }

  const inputCls = "w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-[#10a37f] focus:ring-2 focus:ring-[#10a37f]/10 outline-none transition-all bg-white text-gray-900 placeholder:text-gray-400";

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-[900px] grid grid-cols-1 md:grid-cols-[1fr_400px] gap-5 items-stretch">

        {/* Left — Branding */}
        <div className="bg-[#202123] rounded-2xl p-8 flex flex-col justify-between text-white">
          <div>
            <div className="w-12 h-12 rounded-xl bg-[#10a37f] flex items-center justify-center mb-6">
              <span className="material-symbols-outlined text-white text-2xl" style={{ fontVariationSettings: "'FILL' 1" }}>bolt</span>
            </div>
            <span className="text-[#10a37f] text-[11px] font-bold tracking-widest uppercase">Secure Internal Workspace</span>
            <h1 className="text-3xl font-bold mt-2 mb-3 leading-tight">{appName}</h1>
            <p className="text-gray-400 leading-relaxed text-sm">
              AI internal PT Sinar Jernih Suksesindo berbasis RBAC, knowledge base, audit trail, dan voice lokal.
            </p>
          </div>
          <div className="mt-8 space-y-3">
            {[
              { icon: "lock", text: "Role-based access control (RBAC)" },
              { icon: "library_books", text: "Knowledge base dengan ACL" },
              { icon: "assignment", text: "Audit trail & compliance logging" },
              { icon: "mic", text: "Voice AI lokal & aman" },
            ].map((f) => (
              <div key={f.icon} className="flex items-center gap-3 text-sm text-gray-400">
                <span className="material-symbols-outlined text-[#10a37f] text-[18px]">{f.icon}</span>
                {f.text}
              </div>
            ))}
          </div>
        </div>

        {/* Right — Form */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden flex flex-col">
          {/* Accent bar */}
          <div className="h-1 bg-gradient-to-r from-[#202123] to-[#10a37f]" />

          <div className="flex-1 p-7 flex flex-col">
            <div className="mb-6">
              <span className="text-[#10a37f] text-[11px] font-bold tracking-widest uppercase block mb-1">Access Portal</span>
              <h2 className="text-xl font-bold text-gray-900">{showBootstrap ? "Buat Super Admin" : "Masuk"}</h2>
            </div>

            {error && (
              <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">
                <span className="material-symbols-outlined text-[18px] flex-shrink-0 mt-0.5">error</span>
                {error}
              </div>
            )}

            <form onSubmit={showBootstrap ? bootstrap : submit} className="flex flex-col gap-4 flex-1">
              {showBootstrap && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Nama Lengkap</label>
                  <input className={inputCls} value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Super Admin" required />
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Email</label>
                <input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@perusahaan.com" required autoComplete="email" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Password</label>
                <div className="relative">
                  <input
                    className={`${inputCls} pr-11`}
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete={showBootstrap ? "new-password" : "current-password"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <span className="material-symbols-outlined text-[20px]">{showPassword ? "visibility_off" : "visibility"}</span>
                  </button>
                </div>
              </div>

              <div className="mt-auto pt-4 space-y-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-xl bg-[#202123] hover:bg-[#10a37f] text-white font-bold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      {showBootstrap ? "Membuat..." : "Masuk..."}
                    </>
                  ) : (
                    showBootstrap ? "Buat Super Admin & Masuk" : "Masuk"
                  )}
                </button>

                <button
                  type="button"
                  disabled={loading}
                  onClick={() => { setShowBootstrap(!showBootstrap); setError(""); }}
                  className="w-full py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {showBootstrap ? "← Kembali ke Login" : "Buat Super Admin Pertama"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
